import { createRouter, createWebHistory } from "vue-router";
import Home from "@/views/Home.vue";
import { getCurrentUser, clearUserCache } from "@/lib/api";

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: "/",
      name: "Home",
      component: Home,
    },
    {
      path: "/login",
      name: "Login",
      component: () => import("@/views/Login.vue"),
      meta: { requiresGuest: true },
    },
    {
      path: "/signup",
      name: "Signup",
      component: () => import("@/views/Signup.vue"),
      meta: { requiresGuest: true },
    },
    {
      path: "/reset-password",
      name: "ResetPassword",
      component: () => import("@/views/ResetPassword.vue"),
    },
    {
      path: "/dashboard",
      name: "Dashboard",
      component: () => import("@/views/content/Dashboard.vue"),
      meta: { requiresAuth: false },
    },
    {
      path: "/profile",
      name: "Profile",
      component: () => import("@/views/account/Profile.vue"),
      meta: { requiresAuth: false },
    },
    {
      path: "/compose",
      name: "PostComposer",
      component: () => import("@/views/content/PostComposer.vue"),
      meta: { requiresAuth: false },
    },
    {
      path: "/post-editor",
      name: "PostEditor",
      component: () => import("@/views/content/PostEditor.vue"),
      meta: { requiresAuth: false },
    },
    {
      path: "/media",
      name: "MediaLibrary",
      component: () => import("@/views/content/MediaLibrary.vue"),
      meta: { requiresAuth: false },
    },
    {
      path: "/templates",
      name: "Templates",
      component: () => import("@/views/content/Templates.vue"),
      meta: { requiresAuth: false },
    },
    {
      path: "/calendar",
      name: "Calendar",
      component: () => import("@/views/content/Calendar.vue"),
      meta: { requiresAuth: false },
    },
    {
      path: "/queue",
      name: "Queue",
      component: () => import("@/views/content/Queue.vue"),
      meta: { requiresAuth: false },
    },
    {
      path: "/analytics",
      name: "Analytics",
      component: () => import("@/views/analytics/Analytics.vue"),
      meta: { requiresAuth: false },
    },
    {
      path: "/insights",
      name: "Insights",
      component: () => import("@/views/analytics/Insights.vue"),
      meta: { requiresAuth: false },
    },
    {
      path: "/teams",
      name: "Teams",
      component: () => import("@/views/team/Teams.vue"),
      meta: { requiresAuth: false },
    },
    {
      path: "/approvals",
      name: "Approvals",
      component: () => import("@/views/team/Approvals.vue"),
      meta: { requiresAuth: false },
    },
    {
      path: "/launchpad",
      name: "Launchpad",
      component: () => import("@/views/growth/Launchpad.vue"),
      meta: { requiresAuth: false },
    },
    {
      path: "/seo/dashboard",
      name: "SeoIndexingAdvanced",
      component: () => import("@/views/growth/SeoIndexingAdvanced.vue"),
      meta: { requiresAuth: false },
    },
    {
      path: "/projects",
      name: "ProjectSettings",
      component: () => import("@/views/ProjectSettings.vue"),
      meta: { requiresAuth: false },
    },
    {
      path: "/projects/new",
      name: "ProjectCreate",
      component: () => import("@/views/ProjectCreate.vue"),
      meta: { requiresAuth: false },
    },
    {
      path: "/reports",
      name: "Reports",
      component: () => import("@/views/analytics/Reports.vue"),
      meta: { requiresAuth: false },
    },
    {
      path: "/email-campaigns",
      name: "EmailCampaigns",
      component: () => import("@/views/marketing/EmailCampaigns.vue"),
      meta: { requiresAuth: false },
    },
    {
      path: "/sms-campaigns",
      name: "SmsCampaigns",
      component: () => import("@/views/marketing/SmsCampaigns.vue"),
      meta: { requiresAuth: false },
    },
    {
      path: "/crm",
      name: "CRM",
      component: () => import("@/views/marketing/CRM.vue"),
      meta: { requiresAuth: false },
    },
    {
      path: "/automation",
      name: "Automation",
      component: () => import("@/views/marketing/Automation.vue"),
      meta: { requiresAuth: false },
    },
    {
      path: "/blog",
      name: "Blog",
      component: () => import("@/views/web/Blog.vue"),
      meta: { requiresAuth: false },
    },
    {
      path: "/landing-pages",
      name: "LandingPages",
      component: () => import("@/views/web/LandingPages.vue"),
      meta: { requiresAuth: false },
    },
    {
      path: "/ad-manager",
      name: "AdManager",
      component: () => import("@/views/marketing/AdManager.vue"),
      meta: { requiresAuth: false },
    },
    {
      path: "/link-tracker",
      name: "LinkTracker",
      component: () => import("@/views/marketing/LinkTracker.vue"),
      meta: { requiresAuth: false },
    },
    {
      path: "/auth/facebook/callback",
      name: "FacebookCallback",
      component: () => import("@/views/auth/FacebookCallback.vue"),
      meta: { requiresAuth: false },
    },
    {
      path: "/auth/instagram/callback",
      name: "InstagramCallback",
      component: () => import("@/views/auth/InstagramCallback.vue"),
      meta: { requiresAuth: false },
    },
    {
      path: "/auth/tiktok/callback",
      name: "TikTokCallback",
      component: () => import("@/views/auth/TikTokCallback.vue"),
      meta: { requiresAuth: false },
    },
    {
      path: "/auth/linkedin/callback",
      name: "LinkedInCallback",
      component: () => import("@/views/auth/LinkedInCallback.vue"),
      meta: { requiresAuth: false },
    },
    {
      path: "/auth/twitter/callback",
      name: "TwitterCallback",
      component: () => import("@/views/auth/TwitterCallback.vue"),
      meta: { requiresAuth: false },
    },
    {
      path: "/auth/google/callback",
      name: "GoogleCallback",
      component: () => import("@/views/auth/GoogleCallback.vue"),
      meta: { requiresAuth: false },
    },
    {
      path: "/help",
      name: "HelpCenter",
      component: () => import("@/views/account/HelpCenter.vue"),
    },
    {
      path: "/contact",
      name: "ContactUs",
      component: () => import("@/views/ContactUs.vue"),
    },
    {
      path: "/terms-of-service",
      name: "TermsOfService",
      component: () => import("@/views/TermsOfService.vue"),
    },
    {
      path: "/privacy-policy",
      name: "PrivacyPolicy",
      component: () => import("@/views/PrivacyPolicy.vue"),
    },
    {
      path: "/cookie-policy",
      name: "CookiePolicy",
      component: () => import("@/views/CookiePolicy.vue"),
    },
    {
      path: "/gdpr",
      name: "GDPR",
      component: () => import("@/views/GDPR.vue"),
    },
    {
      path: "/pricing",
      name: "Pricing",
      component: () => import("@/views/Pricing.vue"),
    },
    {
      path: "/podcast-distribution",
      name: "PodcastDistribution",
      component: () => import("@/views/content/PodcastDistribution.vue"),
      meta: { requiresAuth: false },
    },
    {
      path: "/roadmap",
      name: "Roadmap",
      component: () => import("@/views/Roadmap.vue"),
      meta: { requiresAuth: false },
    },
    {
      path: "/inbox",
      name: "Inbox",
      component: () => import("@/views/content/Inbox.vue"),
      meta: { requiresAuth: false },
    },
    {
      path: "/link-in-bio",
      name: "LinkInBio",
      component: () => import("@/views/content/LinkInBio.vue"),
      meta: { requiresAuth: false },
    },
  ],
});

// Navigation guard for authentication
router.beforeEach(async (to) => {
  try {
    const user = await getCurrentUser();
    const isAuthenticated = !!user;

    // Redirect authenticated users away from guest-only pages
    if (to.meta.requiresGuest && isAuthenticated) {
      return { name: "Dashboard" };
    }

    // Redirect unauthenticated users away from protected pages
    if (to.meta.requiresAuth && !isAuthenticated) {
      return { name: "Login" };
    }
  } catch (error) {
    // If there's an error checking auth, redirect to login for protected routes
    if (to.meta.requiresAuth) {
      return { name: "Login" };
    }
  }

  // Allow navigation to continue
  return true;
});

// Function to clear authentication cache (called on login/logout)
export const clearAuthCache = () => {
  clearUserCache();
};

export default router;
